// Semantic status roles — the stable identity workflow actions + kanban drag
// target, independent of an option's display label. Keeps "rename the option"
// from silently breaking the ticket workflow. Mirrors backend actions.py.

import type { StatusRole } from "@/lib/types";

export const STATUS_ROLES: StatusRole[] = ["open", "in_progress", "waiting", "closed"];

// German labels for the role picker UI (AddColumnDialog).
export const STATUS_ROLE_LABELS: Record<StatusRole, string> = {
  open: "Offen",
  in_progress: "In Bearbeitung",
  waiting: "Wartet auf Kunde",
  closed: "Geschlossen",
};

// Canonical German label (lowercased) → role. Legacy fallback for options
// that predate the `role` field or were added inline without one.
//
// 🔴 The closed labels MUST stay in lockstep with the backend's
// `open_work._CLOSED_LABELS` ({geschlossen, abgeschlossen, erledigt, fertig, done, closed}).
// Inline-created options carry NO role by construction, so a user who adds "Fertig" to the
// Aufgaben board's Status column parks work in an option only ONE side calls closed: the
// backend's subtask_rollups counted it done in the Protokoll ("4 von 5 erledigt") while the
// panel showed it unticked — and ticking it silently rewrote the status away from "Fertig".
// The minutes and the panel read one label set, or they lie to each other.
const CLOSED_LABELS = [
  "geschlossen",
  "abgeschlossen",
  "erledigt",
  "fertig",
  "done",
  "closed",
] as const;

const CANONICAL_LABEL_TO_ROLE: Record<string, StatusRole> = {
  offen: "open",
  "in bearbeitung": "in_progress",
  "wartet auf kunde": "waiting",
  ...Object.fromEntries(CLOSED_LABELS.map((l) => [l, "closed" as StatusRole])),
};

// Resolve an option's semantic role: explicit role wins, else fall back to its
// canonical German label. Returns null for plain (non-workflow) options.
export function roleOf(
  option: { role?: StatusRole | null; label?: string } | null | undefined,
): StatusRole | null {
  if (!option) return null;
  if (option.role) return option.role;
  return CANONICAL_LABEL_TO_ROLE[(option.label ?? "").trim().toLowerCase()] ?? null;
}

// True for the lifecycle "workflow" status column — the one whose options carry
// semantic roles (open/in_progress/waiting/closed). Category-style status
// columns (Bereich, Priorität, Vorgangstyp) carry none. On ticket boards only
// the workflow column stays action-only; category status columns are editable.
//
// Same "a column is workflow if ANY option is role-bearing" shape as the backend
// `is_workflow_status_column`. The two agree whenever options carry explicit
// roles (all current workflow columns do — templates set them, the IT migration
// backfilled them), and the role-less CLOSED labels above are the same set the
// backend's `_is_closed_option` uses, so an inline-added "Fertig" reads closed on
// both sides.
export function isWorkflowStatusColumn(
  column:
    | {
        type?: string;
        options?: ({ role?: StatusRole | null; label?: string } | null)[] | null;
      }
    | null
    | undefined,
): boolean {
  if (!column || column.type !== "status") return false;
  return (column.options ?? []).some((o) => roleOf(o) !== null);
}

// Coarse "Status" rollup — the derived category each granular status belongs to.
// This is NEVER a stored column; it's computed from an option's role and used
// for the Kanban "Status (Kategorie)" grouping + at-a-glance views. Maps the
// 4 roles onto the 3 coarse buckets the IT team asked for (waiting folds into
// "In Arbeit"; there is deliberately no "Keine Kategorie" — that was the
// two-status-columns drift artifact).
export type StatusBucket = "todo" | "in_work" | "done";

export const STATUS_BUCKETS: StatusBucket[] = ["todo", "in_work", "done"];

export const BUCKET_OF_ROLE: Record<StatusRole, StatusBucket> = {
  open: "todo",
  in_progress: "in_work",
  waiting: "in_work",
  closed: "done",
};

// The coarse bucket for an option, or null for a non-workflow option.
export function bucketOf(
  option: { role?: StatusRole | null; label?: string } | null | undefined,
): StatusBucket | null {
  const role = roleOf(option);
  return role ? BUCKET_OF_ROLE[role] : null;
}
